import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { TrendingUp, Package, Calendar, FileText, DollarSign, RefreshCw, Search, Filter, X, BarChart3, List, PieChart } from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import Targets from './Targets';
import EnhancedDashboardWidgets from './EnhancedDashboardWidgets';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chart colors
const CHART_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];

const Dashboard = ({ user, onNavigate }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailView, setDetailView] = useState(null);
  const [detailData, setDetailData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'chart'

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/metrics`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetails = async (metricType) => {
    console.log('🔍 fetchDetails called with metricType:', metricType);
    try {
      const response = await axios.get(`${API}/dashboard/details/${metricType}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      console.log('✅ API response received for', metricType, response.data);
      
      setDetailData(response.data);
      setDetailView(metricType);
      console.log('✅ Detail view set to:', metricType);
    } catch (error) {
      console.error('❌ Error fetching details:', error);
      toast.error('Failed to fetch details');
    }
  };

  // Get title and description for detail view
  const getDetailTitle = (metricType) => {
    const titles = {
      'products_6_months_plus': 'Products with Stock > 6 Months Available',
      'products_3_months_less': 'Products with Stock < 3 Months Available',
      'total_products': 'All Products with Expiry Date',
      'total_orders': 'Active Orders',
      'products_in_planning': 'Products in Planning',
      'total_merged_value': 'Merged Stock - All Products',
      'total_value_srh': 'SRH Stock - Value Breakdown',
      'total_value_winlife': 'WINLIFE Stock - Value Breakdown'
    };
    return titles[metricType] || metricType.replace(/_/g, ' ');
  };

  const getDetailDescription = (metricType) => {
    const descriptions = {
      'products_6_months_plus': 'High stock coverage - these products have more than 6 months of inventory available',
      'products_3_months_less': 'Low stock coverage - these products have less than 3 months of inventory remaining',
      'total_products': 'Complete product inventory with expiry dates across all locations',
      'total_orders': 'Active orders currently in progress - sorted by most recent',
      'products_in_planning': 'Products scheduled for manufacturing',
      'total_merged_value': 'Combined inventory value from SRH and WINLIFE',
      'total_value_srh': 'Inventory value at SRH location',
      'total_value_winlife': 'Inventory value at WINLIFE location'
    };
    return descriptions[metricType] || '';
  };

  // Generate chart data based on metric type
  const getChartData = (metricType, data) => {
    if (!data || data.length === 0) return { pieData: [], barData: [] };
    
    switch(metricType) {
      case 'total_products':
      case 'products_6_months_plus':
      case 'products_3_months_less': {
        // Group by manufacturer for pie chart
        const byManufacturer = data.reduce((acc, item) => {
          const mfr = item.manufactured_by || 'Unknown';
          acc[mfr] = (acc[mfr] || 0) + 1;
          return acc;
        }, {});
        const pieData = Object.entries(byManufacturer)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([name, value]) => ({ name, value }));
        
        // Top 10 products by stock months for bar chart
        const barData = [...data]
          .sort((a, b) => (b.available_months || 0) - (a.available_months || 0))
          .slice(0, 10)
          .map(item => ({
            name: (item.product_name || '').substring(0, 15) + '...',
            fullName: item.product_name,
            months: item.available_months || 0,
            qty: (item.qty_srh || 0) + (item.qty_winlife || 0)
          }));
        return { pieData, barData };
      }
      
      case 'total_orders': {
        // Group by order_given_to for pie chart
        const bySupplier = data.reduce((acc, item) => {
          const supplier = item.order_given_to || 'Unknown';
          acc[supplier] = (acc[supplier] || 0) + (item.order_qty || 0);
          return acc;
        }, {});
        const pieData = Object.entries(bySupplier)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([name, value]) => ({ name, value }));
        
        // Orders by days since order
        const barData = [...data]
          .sort((a, b) => (a.days_since_order || 0) - (b.days_since_order || 0))
          .slice(0, 15)
          .map(item => ({
            name: (item.product_name || '').substring(0, 12) + '...',
            fullName: item.product_name,
            days: item.days_since_order || 0,
            qty: item.order_qty || 0
          }));
        return { pieData, barData };
      }
      
      case 'products_in_planning': {
        // Group by order_to for pie chart
        const bySupplier = data.reduce((acc, item) => {
          const supplier = item.order_to || 'Unknown';
          acc[supplier] = (acc[supplier] || 0) + (item.qty || 0);
          return acc;
        }, {});
        const pieData = Object.entries(bySupplier)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([name, value]) => ({ name, value }));
        
        const barData = [...data]
          .sort((a, b) => (b.qty || 0) - (a.qty || 0))
          .slice(0, 10)
          .map(item => ({
            name: (item.product_name || '').substring(0, 12) + '...',
            fullName: item.product_name,
            qty: item.qty || 0
          }));
        return { pieData, barData };
      }
      
      case 'total_merged_value':
      case 'total_value_srh':
      case 'total_value_winlife': {
        // Group by manufacturer for pie chart (by value)
        const byManufacturer = data.reduce((acc, item) => {
          const mfr = item.manufactured_by || 'Unknown';
          acc[mfr] = (acc[mfr] || 0) + (item.total_value || item.value_srh || item.value_winlife || 0);
          return acc;
        }, {});
        const pieData = Object.entries(byManufacturer)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([name, value]) => ({ name, value: Math.round(value) }));
        
        // Top 10 products by value
        const barData = [...data]
          .sort((a, b) => (b.total_value || 0) - (a.total_value || 0))
          .slice(0, 10)
          .map(item => ({
            name: (item.product_name || '').substring(0, 12) + '...',
            fullName: item.product_name,
            value: Math.round(item.total_value || item.value_srh || item.value_winlife || 0),
            qty: (item.qty_srh || 0) + (item.qty_winlife || 0)
          }));
        return { pieData, barData };
      }
      
      default:
        return { pieData: [], barData: [] };
    }
  };

  const getSortOptions = (metricType) => {
    // Return relevant sort options based on metric type
    if (metricType === 'total_orders') {
      return [
        { label: 'Most Recent Order', field: 'days_since_order', order: 'asc' },
        { label: 'Oldest Order', field: 'days_since_order', order: 'desc' },
        { label: 'Product Name (A-Z)', field: 'product_name', order: 'asc' },
        { label: 'Product Name (Z-A)', field: 'product_name', order: 'desc' },
        { label: 'Quantity (High-Low)', field: 'order_qty', order: 'desc' },
        { label: 'Quantity (Low-High)', field: 'order_qty', order: 'asc' }
      ];
    } else if (metricType === 'products_in_planning') {
      return [
        { label: 'Product Name (A-Z)', field: 'product_name', order: 'asc' },
        { label: 'Product Name (Z-A)', field: 'product_name', order: 'desc' },
        { label: 'Order Date (Newest)', field: 'order_given_date', order: 'desc' },
        { label: 'Order Date (Oldest)', field: 'order_given_date', order: 'asc' },
        { label: 'Quantity (High-Low)', field: 'qty', order: 'desc' },
        { label: 'Quantity (Low-High)', field: 'qty', order: 'asc' }
      ];
    } else if (metricType === 'total_value_srh') {
      return [
        { label: 'Total Value (High-Low)', field: 'total_value', order: 'desc' },
        { label: 'Total Value (Low-High)', field: 'total_value', order: 'asc' },
        { label: 'Product Name (A-Z)', field: 'product_name', order: 'asc' },
        { label: 'Product Name (Z-A)', field: 'product_name', order: 'desc' },
        { label: 'Qty SRH (High-Low)', field: 'qty_srh', order: 'desc' },
        { label: 'Qty SRH (Low-High)', field: 'qty_srh', order: 'asc' },
        { label: 'Cost@SRH (High-Low)', field: 'cost_srh', order: 'desc' },
        { label: 'Cost@SRH (Low-High)', field: 'cost_srh', order: 'asc' }
      ];
    } else if (metricType === 'total_value_winlife') {
      return [
        { label: 'Total Value (High-Low)', field: 'total_value', order: 'desc' },
        { label: 'Total Value (Low-High)', field: 'total_value', order: 'asc' },
        { label: 'Product Name (A-Z)', field: 'product_name', order: 'asc' },
        { label: 'Product Name (Z-A)', field: 'product_name', order: 'desc' },
        { label: 'Qty WINLIFE (High-Low)', field: 'qty_winlife', order: 'desc' },
        { label: 'Qty WINLIFE (Low-High)', field: 'qty_winlife', order: 'asc' },
        { label: 'Cost@WINLIFE (High-Low)', field: 'cost_winlife', order: 'desc' },
        { label: 'Cost@WINLIFE (Low-High)', field: 'cost_winlife', order: 'asc' }
      ];
    } else if (metricType.includes('value')) {
      return [
        { label: 'Value (High-Low)', field: 'total_value', order: 'desc' },
        { label: 'Value (Low-High)', field: 'total_value', order: 'asc' },
        { label: 'Product Name (A-Z)', field: 'product_name', order: 'asc' },
        { label: 'Product Name (Z-A)', field: 'product_name', order: 'desc' },
        { label: 'Quantity (High-Low)', field: 'qty', order: 'desc' },
        { label: 'Quantity (Low-High)', field: 'qty', order: 'asc' }
      ];
    } else {
      return [
        { label: 'Product Name (A-Z)', field: 'product_name', order: 'asc' },
        { label: 'Product Name (Z-A)', field: 'product_name', order: 'desc' },
        { label: 'Quantity (High-Low)', field: 'qty', order: 'desc' },
        { label: 'Quantity (Low-High)', field: 'qty', order: 'asc' }
      ];
    }
  };

  // Filter and sort data - MUST be before any conditional returns
  const filteredAndSortedData = useMemo(() => {
    if (!Array.isArray(detailData) || detailData.length === 0) return [];

    let filtered = detailData;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = detailData.filter(item => {
        return Object.values(item).some(value => 
          String(value).toLowerCase().includes(search)
        );
      });
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];

        // Special handling for expiry_date column
        if (sortField === 'expiry_date') {
          const parseExpiryDate = (dateStr) => {
            if (!dateStr || dateStr === '' || dateStr === 'NaT') return null;
            
            const str = String(dateStr).toLowerCase().trim();
            
            // Try MMM-YYYY format first (e.g., "jan-2027")
            const monthMap = {
              'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
              'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
            };
            
            const mmmYYYY = str.match(/^([a-z]{3})-(\d{4})$/);
            if (mmmYYYY) {
              const month = monthMap[mmmYYYY[1]];
              const year = parseInt(mmmYYYY[2], 10);
              if (month !== undefined && !isNaN(year)) {
                return new Date(year, month, 1);
              }
            }
            
            // Try YYYY-MM-DD format (e.g., "2025-11-03" or "2025-11-03 00:00:00")
            const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (isoMatch) {
              const year = parseInt(isoMatch[1], 10);
              const month = parseInt(isoMatch[2], 10) - 1;
              const day = parseInt(isoMatch[3], 10);
              if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                return new Date(year, month, day);
              }
            }
            
            // Try DD/MM/YYYY format
            const ddmmyyyy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (ddmmyyyy) {
              const day = parseInt(ddmmyyyy[1], 10);
              const month = parseInt(ddmmyyyy[2], 10) - 1;
              const year = parseInt(ddmmyyyy[3], 10);
              if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                return new Date(year, month, day);
              }
            }
            
            return null;
          };

          const aDate = parseExpiryDate(aVal);
          const bDate = parseExpiryDate(bVal);

          // Handle null dates - put them at the end
          if (!aDate && !bDate) return 0;
          if (!aDate) return sortOrder === 'asc' ? 1 : -1;
          if (!bDate) return sortOrder === 'asc' ? -1 : 1;

          return sortOrder === 'asc' 
            ? aDate.getTime() - bDate.getTime() 
            : bDate.getTime() - aDate.getTime();
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal || '').toLowerCase();
        const bStr = String(bVal || '').toLowerCase();
        
        if (sortOrder === 'asc') {
          return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
        } else {
          return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
        }
      });
    }

    return filtered;
  }, [detailData, searchTerm, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (detailView) {
    const title = getDetailTitle(detailView);
    const description = getDetailDescription(detailView);
    const sortOptions = getSortOptions(detailView);
    const chartData = getChartData(detailView, detailData);
    
    // Define custom column order for specific views
    let columns = [];
    if (detailView === 'total_orders' && detailData.length > 0) {
      columns = ['product_name', 'order_given_to', 'order_qty', 'order_given_date', 'days_since_order'];
    } else if (detailView === 'products_in_planning' && detailData.length > 0) {
      columns = ['product_name', 'order_to', 'order_given_date', 'qty'];
    } else {
      columns = detailData.length > 0 ? Object.keys(detailData[0]) : [];
    }
    
    // Auto-apply default sorting for orders (most recent first)
    if (detailView === 'total_orders' && !sortField && detailData.length > 0) {
      setSortField('days_since_order');
      setSortOrder('asc');
    }
    
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <Button onClick={() => {
            setDetailView(null);
            setSearchTerm('');
            setSortField('');
            setSortOrder('asc');
            setViewMode('table');
          }} variant="outline" size="sm" className="h-9">
            ← <span className="hidden sm:inline ml-1">Back to Dashboard</span><span className="sm:hidden ml-1">Back</span>
          </Button>
          
          {/* View Mode Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                viewMode === 'table' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <List className="w-4 h-4" /> Table
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                viewMode === 'chart' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <BarChart3 className="w-4 h-4" /> Charts
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 lg:p-6">
          {/* Title and Summary */}
          <div className="mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800 mb-2">{title}</h3>
            <p className="text-sm sm:text-base text-slate-600 mb-2">
              Showing <span className="font-semibold text-indigo-600">{filteredAndSortedData.length}</span> of {detailData.length} {detailView.includes('orders') ? 'orders' : 'products'}
            </p>
            {description && (
              <p className={`text-xs sm:text-sm ${
                detailView === 'products_6_months_plus' ? 'text-red-600 font-medium' :
                detailView === 'products_3_months_less' ? 'text-orange-600 font-medium' :
                'text-slate-500'
              }`}>
                {detailView === 'products_6_months_plus' && '⚠️ '}
                {detailView === 'products_3_months_less' && '⚠️ '}
                {description}
              </p>
            )}
          </div>
          
          {/* Chart View */}
          {viewMode === 'chart' && (
            <div className="space-y-8">
              {/* Pie Chart */}
              {chartData.pieData.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">
                    {detailView.includes('value') ? 'Value Distribution by Manufacturer' : 
                     detailView.includes('orders') ? 'Order Quantity by Supplier' :
                     detailView.includes('planning') ? 'Quantity by Supplier' :
                     'Products by Manufacturer'}
                  </h4>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={chartData.pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          dataKey="value"
                        >
                          {chartData.pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => detailView.includes('value') 
                            ? `₹${value.toLocaleString('en-IN')}` 
                            : value.toLocaleString('en-IN')
                          } 
                        />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              
              {/* Bar Chart */}
              {chartData.barData.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">
                    {detailView.includes('value') ? 'Top 10 Products by Value' :
                     detailView.includes('orders') ? 'Orders by Days Pending' :
                     detailView.includes('planning') ? 'Top Products by Quantity' :
                     'Top 10 Products by Stock Months'}
                  </h4>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData.barData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} fontSize={11} />
                        <YAxis 
                          tickFormatter={(value) => detailView.includes('value') 
                            ? `₹${(value/1000).toFixed(0)}K` 
                            : value.toLocaleString()
                          } 
                        />
                        <Tooltip 
                          formatter={(value, name) => {
                            if (name === 'value') return [`₹${value.toLocaleString('en-IN')}`, 'Value'];
                            if (name === 'months') return [value, 'Stock Months'];
                            if (name === 'days') return [value, 'Days Pending'];
                            return [value.toLocaleString('en-IN'), name];
                          }}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                        />
                        <Legend />
                        {detailView.includes('value') && <Bar dataKey="value" fill="#6366f1" name="Value (₹)" />}
                        {detailView.includes('orders') && <Bar dataKey="days" fill="#f59e0b" name="Days Pending" />}
                        {detailView.includes('orders') && <Bar dataKey="qty" fill="#22c55e" name="Order Qty" />}
                        {detailView.includes('planning') && <Bar dataKey="qty" fill="#8b5cf6" name="Quantity" />}
                        {(detailView === 'total_products' || detailView.includes('months')) && (
                          <>
                            <Bar dataKey="months" fill="#3b82f6" name="Stock Months" />
                            <Bar dataKey="qty" fill="#22c55e" name="Total Qty" />
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              
              {/* Summary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{detailData.length}</div>
                  <div className="text-sm text-blue-700">Total Records</div>
                </div>
                {detailView.includes('value') && (
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      ₹{Math.round(detailData.reduce((sum, item) => sum + (item.total_value || 0), 0) / 100000)}L
                    </div>
                    <div className="text-sm text-green-700">Total Value</div>
                  </div>
                )}
                {detailView.includes('orders') && (
                  <>
                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {Math.round(detailData.reduce((sum, item) => sum + (item.days_since_order || 0), 0) / detailData.length) || 0}
                      </div>
                      <div className="text-sm text-orange-700">Avg Days Pending</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {detailData.reduce((sum, item) => sum + (item.order_qty || 0), 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-purple-700">Total Order Qty</div>
                    </div>
                  </>
                )}
                {(detailView === 'total_products' || detailView.includes('months')) && (
                  <>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {detailData.reduce((sum, item) => sum + (item.qty_srh || 0), 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-green-700">Total SRH Qty</div>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-teal-600">
                        {detailData.reduce((sum, item) => sum + (item.qty_winlife || 0), 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-teal-700">Total WINLIFE Qty</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Table View - Search and Filters */}
          {viewMode === 'table' && (
            <>
              {/* Search and Filters */}
              <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
                {/* Search Box */}
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={`Search ${detailView.includes('orders') ? 'orders' : 'products'}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10"
                  />
                </div>

                {/* Sort Dropdown */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-500 hidden sm:block" />
                  <select
                    value={`${sortField}|${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('|');
                      setSortField(field);
                      setSortOrder(order);
                    }}
                    className="w-full sm:w-auto px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 h-10"
                  >
                    <option value="|">Sort by...</option>
                    {sortOptions.map((option, idx) => (
                      <option key={idx} value={`${option.field}|${option.order}`}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

            {/* Clear Filters */}
            {(searchTerm || sortField) && (
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setSortField('');
                  setSortOrder('asc');
                }}
                variant="ghost"
                size="sm"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto border rounded-lg">
            {filteredAndSortedData.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    {/* Show key columns first for stock cards */}
                    {(detailView === 'products_6_months_plus' || detailView === 'products_3_months_less') ? (
                      <>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('product_name')}>
                          <div className="flex items-center gap-2">
                            Product Name {sortField === 'product_name' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-indigo-700 cursor-pointer hover:bg-slate-200 bg-indigo-50" onClick={() => handleSort('available_months')}>
                          <div className="flex items-center gap-2">
                            Available (Months) {sortField === 'available_months' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('qty_srh')}>
                          <div className="flex items-center gap-2">
                            Qty SRH {sortField === 'qty_srh' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('qty_winlife')}>
                          <div className="flex items-center gap-2">
                            Qty WINLIFE {sortField === 'qty_winlife' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('sale_qty_last_month')}>
                          <div className="flex items-center gap-2">
                            Sale Last Month {sortField === 'sale_qty_last_month' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('reorder_3_months')}>
                          <div className="flex items-center gap-2">
                            Reorder 3M {sortField === 'reorder_3_months' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                      </>
                    ) : detailView === 'total_value_srh' ? (
                      <>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('product_name')}>
                          <div className="flex items-center gap-2">
                            Product Name {sortField === 'product_name' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('qty_srh')}>
                          <div className="flex items-center gap-2">
                            Qty SRH {sortField === 'qty_srh' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('cost_srh')}>
                          <div className="flex items-center gap-2">
                            Cost@SRH {sortField === 'cost_srh' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('manufactured_by')}>
                          <div className="flex items-center gap-2">
                            Manufactured By {sortField === 'manufactured_by' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-indigo-700 cursor-pointer hover:bg-slate-200 bg-indigo-50" onClick={() => handleSort('total_value')}>
                          <div className="flex items-center gap-2">
                            Total Value {sortField === 'total_value' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                      </>
                    ) : detailView === 'total_value_winlife' ? (
                      <>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('product_name')}>
                          <div className="flex items-center gap-2">
                            Product Name {sortField === 'product_name' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('qty_winlife')}>
                          <div className="flex items-center gap-2">
                            Qty WINLIFE {sortField === 'qty_winlife' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('cost_winlife')}>
                          <div className="flex items-center gap-2">
                            Cost@WINLIFE {sortField === 'cost_winlife' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('manufactured_by')}>
                          <div className="flex items-center gap-2">
                            Manufactured By {sortField === 'manufactured_by' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-orange-700 cursor-pointer hover:bg-slate-200 bg-orange-50" onClick={() => handleSort('expiry_date')}>
                          <div className="flex items-center gap-2">
                            Expiry Date {sortField === 'expiry_date' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-indigo-700 cursor-pointer hover:bg-slate-200 bg-indigo-50" onClick={() => handleSort('total_value')}>
                          <div className="flex items-center gap-2">
                            Total Value {sortField === 'total_value' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                      </>
                    ) : detailView === 'total_merged_value' ? (
                      <>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('product_name')}>
                          <div className="flex items-center gap-2">
                            Product Name {sortField === 'product_name' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('qty_srh')}>
                          <div className="flex items-center gap-2">
                            Qty @ SRH {sortField === 'qty_srh' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('qty_winlife')}>
                          <div className="flex items-center gap-2">
                            Qty @ WINLIFE {sortField === 'qty_winlife' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('cost_srh')}>
                          <div className="flex items-center gap-2">
                            Cost @ SRH {sortField === 'cost_srh' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('cost_winlife')}>
                          <div className="flex items-center gap-2">
                            Cost @ WINLIFE {sortField === 'cost_winlife' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('value_srh')}>
                          <div className="flex items-center gap-2">
                            Value @ SRH {sortField === 'value_srh' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('value_winlife')}>
                          <div className="flex items-center gap-2">
                            Value @ WINLIFE {sortField === 'value_winlife' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-blue-700 cursor-pointer hover:bg-slate-200 bg-blue-50" onClick={() => handleSort('total_value')}>
                          <div className="flex items-center gap-2">
                            Total Value {sortField === 'total_value' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                      </>
                    ) : detailView === 'total_products' ? (
                      <>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('product_name')}>
                          <div className="flex items-center gap-2">
                            Product Name {sortField === 'product_name' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('qty_srh')}>
                          <div className="flex items-center gap-2">
                            Qty @ SRH {sortField === 'qty_srh' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('qty_winlife')}>
                          <div className="flex items-center gap-2">
                            Qty @ WINLIFE {sortField === 'qty_winlife' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('available_qty_months')}>
                          <div className="flex items-center gap-2">
                            Available Qty (Months) {sortField === 'available_qty_months' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-green-700 cursor-pointer hover:bg-slate-200 bg-green-50" onClick={() => handleSort('reorder_3_months')}>
                          <div className="flex items-center gap-2">
                            Reorder 3M {sortField === 'reorder_3_months' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-orange-700 cursor-pointer hover:bg-slate-200 bg-orange-50" onClick={() => handleSort('reorder_6_months')}>
                          <div className="flex items-center gap-2">
                            Reorder 6M {sortField === 'reorder_6_months' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-red-700 cursor-pointer hover:bg-slate-200 bg-red-50" onClick={() => handleSort('reorder_9_months')}>
                          <div className="flex items-center gap-2">
                            Reorder 9M {sortField === 'reorder_9_months' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200" onClick={() => handleSort('manufactured_by')}>
                          <div className="flex items-center gap-2">
                            Manufactured By {sortField === 'manufactured_by' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-purple-700 cursor-pointer hover:bg-slate-200 bg-purple-50" onClick={() => handleSort('expiry_date')}>
                          <div className="flex items-center gap-2">
                            Expiry Date {sortField === 'expiry_date' && <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                          </div>
                        </th>
                      </>
                    ) : (
                      columns.slice(0, 10).map((key) => {
                        // Custom labels for specific columns
                        const columnLabels = {
                          'days_since_order': 'Days Since Order',
                          'order_given_date': 'Order Date',
                          'order_given_to': 'Supplier',
                          'order_qty': 'Quantity',
                          'product_name': 'Product Name',
                          'order_to': 'Order To',
                          'qty': 'Quantity'
                        };
                        const label = columnLabels[key] || key.replace(/_/g, ' ');
                        
                        return (
                          <th 
                            key={key} 
                            className="px-4 py-3 text-left font-semibold text-slate-700 capitalize cursor-pointer hover:bg-slate-200 transition-colors"
                            onClick={() => handleSort(key)}
                          >
                            <div className="flex items-center gap-2">
                              {label}
                              {sortField === key && (
                                <span className="text-xs">
                                  {sortOrder === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                        );
                      })
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedData.map((item, idx) => {
                    // Determine row highlighting based on card type
                    let rowClass = 'border-t border-slate-200 hover:bg-slate-50 transition-colors';
                    
                    if (detailView === 'products_6_months_plus' && item.available_months > 12) {
                      rowClass += ' bg-red-50'; // More than 1 year of stock
                    } else if (detailView === 'products_3_months_less') {
                      if (item.available_months < 1) {
                        rowClass += ' bg-red-100'; // Critical - less than 1 month
                      } else if (item.available_months < 2) {
                        rowClass += ' bg-orange-50'; // Warning - less than 2 months
                      }
                    }
                    
                    return (
                      <tr key={idx} className={rowClass}>
                        {(detailView === 'products_6_months_plus' || detailView === 'products_3_months_less') ? (
                          <>
                            <td className="px-4 py-3 text-slate-600 font-medium">{item.product_name || '-'}</td>
                            <td className="px-4 py-3 text-indigo-700 font-bold bg-indigo-50">
                              {item.available_months ? `${item.available_months} months` : '-'}
                            </td>
                            <td className="px-4 py-3 text-slate-600">{item.qty_srh?.toLocaleString() || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{item.qty_winlife?.toLocaleString() || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{item.sale_qty_last_month?.toLocaleString() || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{item.reorder_3_months?.toLocaleString() || '-'}</td>
                          </>
                        ) : detailView === 'total_orders' ? (
                          // Custom rendering for total_orders with specific column order
                          columns.map((key) => {
                            const value = item[key];
                            let displayValue = value || '-';
                            let cellClass = "px-4 py-3 text-slate-600";
                            
                            // Special formatting for days_since_order
                            if (key === 'days_since_order') {
                              displayValue = value !== undefined ? `${value} days` : '-';
                              // Color code based on age
                              if (value > 90) {
                                cellClass = "px-4 py-3 text-red-700 font-semibold bg-red-50";
                              } else if (value > 60) {
                                cellClass = "px-4 py-3 text-orange-700 font-semibold bg-orange-50";
                              } else if (value > 30) {
                                cellClass = "px-4 py-3 text-yellow-700 font-semibold bg-yellow-50";
                              } else {
                                cellClass = "px-4 py-3 text-green-700 font-semibold bg-green-50";
                              }
                            } else if (key === 'order_qty') {
                              displayValue = typeof value === 'number' ? value.toLocaleString() : value || '-';
                            }
                            
                            return (
                              <td key={key} className={cellClass}>
                                {displayValue}
                              </td>
                            );
                          })
                        ) : detailView === 'products_in_planning' ? (
                          // Custom rendering for products_in_planning with specific column order
                          columns.map((key) => {
                            const value = item[key];
                            let displayValue = value || '-';
                            let cellClass = "px-4 py-3 text-slate-600";
                            
                            // Format quantity with locale
                            if (key === 'qty') {
                              displayValue = typeof value === 'number' ? value.toLocaleString() : value || '-';
                            }
                            
                            return (
                              <td key={key} className={cellClass}>
                                {displayValue}
                              </td>
                            );
                          })
                        ) : detailView === 'total_products' ? (
                          // Custom rendering for total_products with highlighted reorder columns
                          <>
                            <td className="px-4 py-3 text-slate-600 font-medium">{item.product_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.qty_srh === 'number' ? item.qty_srh.toLocaleString() : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.qty_winlife === 'number' ? item.qty_winlife.toLocaleString() : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">
                              {typeof item.available_qty_months === 'number' ? `${item.available_qty_months.toFixed(2)} months` : '-'}
                            </td>
                            <td className="px-4 py-3 text-green-700 font-semibold bg-green-50">
                              {typeof item.reorder_3_months === 'number' ? item.reorder_3_months.toLocaleString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-orange-700 font-semibold bg-orange-50">
                              {typeof item.reorder_6_months === 'number' ? item.reorder_6_months.toLocaleString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-red-700 font-semibold bg-red-50">
                              {typeof item.reorder_9_months === 'number' ? item.reorder_9_months.toLocaleString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-slate-600">{item.manufactured_by || '-'}</td>
                            <td className="px-4 py-3 text-purple-700 font-medium bg-purple-50">{item.expiry_date || '-'}</td>
                          </>
                        ) : detailView === 'total_value_srh' ? (
                          // Custom rendering for total_value_srh with specific columns
                          <>
                            <td className="px-4 py-3 text-slate-600 font-medium">{item.product_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.qty_srh === 'number' ? item.qty_srh.toLocaleString() : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.cost_srh === 'number' ? `₹${item.cost_srh.toLocaleString()}` : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{item.manufactured_by || '-'}</td>
                            <td className="px-4 py-3 text-indigo-700 font-bold bg-indigo-50">{typeof item.total_value === 'number' ? `₹${item.total_value.toLocaleString()}` : '-'}</td>
                          </>
                        ) : detailView === 'total_value_winlife' ? (
                          // Custom rendering for total_value_winlife with specific columns
                          <>
                            <td className="px-4 py-3 text-slate-600 font-medium">{item.product_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.qty_winlife === 'number' ? item.qty_winlife.toLocaleString() : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.cost_winlife === 'number' ? `₹${item.cost_winlife.toLocaleString()}` : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{item.manufactured_by || '-'}</td>
                            <td className="px-4 py-3 text-orange-700 font-semibold bg-orange-50">{item.expiry_date || '-'}</td>
                            <td className="px-4 py-3 text-indigo-700 font-bold bg-indigo-50">{typeof item.total_value === 'number' ? `₹${item.total_value.toLocaleString()}` : '-'}</td>
                          </>
                        ) : detailView === 'total_merged_value' ? (
                          // Custom rendering for total_merged_value with cost and value columns
                          <>
                            <td className="px-4 py-3 text-slate-600 font-medium">{item.product_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.qty_srh === 'number' ? item.qty_srh.toLocaleString() : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.qty_winlife === 'number' ? item.qty_winlife.toLocaleString() : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.cost_srh === 'number' ? `₹${item.cost_srh.toFixed(2)}` : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.cost_winlife === 'number' ? `₹${item.cost_winlife.toFixed(2)}` : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.value_srh === 'number' ? `₹${item.value_srh.toLocaleString()}` : '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{typeof item.value_winlife === 'number' ? `₹${item.value_winlife.toLocaleString()}` : '-'}</td>
                            <td className="px-4 py-3 text-blue-700 font-bold bg-blue-50">
                              {typeof item.value_srh === 'number' && typeof item.value_winlife === 'number' 
                                ? `₹${((item.value_srh || 0) + (item.value_winlife || 0)).toLocaleString()}` 
                                : '-'}
                            </td>
                          </>
                        ) : (
                          Object.entries(item).slice(0, 10).map(([key, value]) => (
                            <td key={key} className="px-4 py-3 text-slate-600">
                              {typeof value === 'number' ? (
                                key.includes('value') || key.includes('cost') || key.includes('price') ? (
                                  `₹${value.toLocaleString()}`
                                ) : (
                                  value.toLocaleString()
                                )
                              ) : (
                                value || '-'
                              )}
                            </td>
                          ))
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="py-12 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">
                  {searchTerm ? 'No products found matching your search' : 'No data available'}
                </p>
              </div>
            )}
          </div>
            </>
          )}

          {/* Footer Alerts - Only show in table view */}
          {viewMode === 'table' && filteredAndSortedData.length > 0 && (
            <>
              {detailView === 'products_6_months_plus' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2">⚠️ High Stock Coverage Alert</h4>
                  <p className="text-sm text-red-700">
                    These {filteredAndSortedData.length} products have more than 6 months of available inventory. 
                    Consider running promotions, offering discounts, or implementing clearance strategies to optimize inventory turnover.
                    {filteredAndSortedData.some(item => item.available_months > 12) && (
                      <span className="block mt-1 font-semibold text-red-700">
                        🔴 Critical: Some products have more than 1 year of stock (highlighted in red)
                      </span>
                    )}
                  </p>
                </div>
              )}

              {detailView === 'products_3_months_less' && (
                <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <h4 className="font-semibold text-orange-800 mb-2">⚠️ Low Stock Coverage Alert</h4>
                  <p className="text-sm text-orange-700">
                    These {filteredAndSortedData.length} products have less than 3 months of stock coverage available.
                    {filteredAndSortedData.some(item => item.available_months < 1) && (
                      <span className="block mt-1 font-semibold text-red-600">
                        🔴 Critical: Some products have less than 1 month of stock (highlighted in red)
                      </span>
                    )}
                  </p>
                </div>
              )}

              {detailView === 'total_orders' && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">📦 Orders Overview</h4>
                  <p className="text-sm text-blue-700">
                    Showing {filteredAndSortedData.length} orders. Use the sort dropdown to view by newest/oldest, 
                    or search for specific products or suppliers.
                  </p>
                </div>
              )}

              {detailView === 'products_in_planning' && (
                <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h4 className="font-semibold text-purple-800 mb-2">🏭 Manufacturing Planning</h4>
                  <p className="text-sm text-purple-700">
                    {filteredAndSortedData.length} products scheduled for manufacturing. 
                    Monitor these products for production timelines and resource allocation.
                  </p>
                </div>
              )}

              {detailView.includes('value') && (
                <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <h4 className="font-semibold text-indigo-800 mb-2">💰 Value Analysis</h4>
                  <p className="text-sm text-indigo-700">
                    Total inventory value breakdown. Use sorting to identify high-value products 
                    or search for specific items to analyze their contribution to total value.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  const metricCards = [
    {
      key: 'total_products',
      title: 'Total Products with Expiry Date',
      value: metrics?.total_products || 0,
      icon: Package,
      color: 'bg-blue-500',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      key: 'products_6_months_plus',
      title: 'Products > 6 Months',
      value: metrics?.products_6_months_plus || 0,
      icon: Calendar,
      color: 'bg-red-500',
      gradient: 'from-red-500 to-red-600'
    },
    {
      key: 'products_3_months_less',
      title: 'Products < 3 Months',
      value: metrics?.products_3_months_less || 0,
      icon: Calendar,
      color: 'bg-green-500',
      gradient: 'from-green-500 to-green-600'
    },
    {
      key: 'total_orders',
      title: 'Total Orders',
      value: metrics?.total_orders || 0,
      icon: FileText,
      color: 'bg-purple-500',
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      key: 'products_in_planning',
      title: 'Products in Planning',
      value: metrics?.products_in_planning || 0,
      icon: TrendingUp,
      color: 'bg-orange-500',
      gradient: 'from-orange-500 to-orange-600'
    },
    {
      key: 'total_merged_value',
      title: 'Total Merged Value',
      value: `₹${metrics?.total_merged_value?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: 'bg-indigo-500',
      gradient: 'from-indigo-500 to-indigo-600'
    },
    {
      key: 'total_value_srh',
      title: 'Total Value at SRH',
      value: `₹${metrics?.total_value_srh?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: 'bg-cyan-500',
      gradient: 'from-cyan-500 to-cyan-600'
    },
    {
      key: 'total_value_winlife',
      title: 'Total Value at WINLIFE',
      value: `₹${metrics?.total_value_winlife?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: 'bg-teal-500',
      gradient: 'from-teal-500 to-teal-600'
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Enhanced Dashboard Summary Widgets */}
      <EnhancedDashboardWidgets user={user} onNavigate={onNavigate} />

      <div className="border-t border-slate-200 my-4 sm:my-8" />

      {/* Targets Section */}
      <Targets user={user} />

      <div className="border-t border-slate-200 my-4 sm:my-8" />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
        <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800">Detailed Metrics</h2>
        <p className="text-xs sm:text-sm text-slate-500">Tap any card for details</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {metricCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.key}
              onClick={() => {
                console.log('Card clicked:', card.key, card.title);
                fetchDetails(card.key);
              }}
              className="bg-white rounded-lg sm:rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden group active:scale-95"
            >
              <div className={`bg-gradient-to-br ${card.gradient} p-2 sm:p-3 lg:p-4`}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
              </div>
              <div className="p-3 sm:p-4 lg:p-5">
                <p className="text-[10px] sm:text-xs lg:text-sm text-slate-600 mb-1 truncate">{card.title}</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800 group-hover:text-indigo-600 transition-colors truncate">
                  {card.value}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
